/**
 * Scope hierarchy labels for Metrics pies and series charts.
 * Inner ring = selected scope(s).
 * Outer / series = SKUs when outerKind is 'sku' (Active logs), else 1st child scopes
 * (Departments → Field operations, Workshop, …).
 */

import {
  ActivityGroupsModel,
  ActivitySkuHierarchyGroup,
  ALL_ACTIVITY_SCOPE_ID,
  activityOccurrenceKey,
  checkedIncludesShowAll,
  collectScopeOccurrenceKeys,
  DEPARTMENTS_METRIC_SCOPE_ID,
  DEPARTMENTS_ROOT_SCOPE_NAME,
  expandActivityGroupsTokens,
  logMatchesActivityGroupsOccurrenceKeys,
  selectionIdFromCheckedToken,
} from 'pages/Logs/activityGroupsModel';
import type { Log } from 'pages/Logs/hooks';
import type { MetricsScopeSelection } from './metricsScopeSelection';

export type MetricsScopeChartNode = {
  id: string,
  name: string,
  parentPath: string[],
  depth: number,
  /** Outer-ring SKU slice — matched by log.skus[].id. */
  isSku?: boolean,
};

const scopeNodeKeysCache = new WeakMap<
ActivityGroupsModel,
Map<string, string[]>
>();

const scopeNodeCacheKey = (nodeId: string, parentPath: string[]) => (
  `${parentPath.join('>')}|${nodeId}`
);

const expandedScopeKeysForNode = (
  model: ActivityGroupsModel,
  node: ActivitySkuHierarchyGroup,
  parentPath: string[],
): string[] => {
  let byModel = scopeNodeKeysCache.get(model);
  if (!byModel) {
    byModel = new Map();
    scopeNodeKeysCache.set(model, byModel);
  }
  const cacheKey = scopeNodeCacheKey(node.id, parentPath);
  const cached = byModel.get(cacheKey);
  if (cached) return cached;
  const keys = collectScopeOccurrenceKeys(node, parentPath);
  byModel.set(cacheKey, keys);
  return keys;
};

const findHierarchyNode = (
  nodes: ActivitySkuHierarchyGroup[],
  id: string,
  parentPath: string[] = [],
): { node: ActivitySkuHierarchyGroup, parentPath: string[] } | null => {
  for (let i = 0; i < (nodes || []).length; i += 1) {
    const node = nodes[i];
    if (node.isObjectGroup) continue;
    if (node.id === id) return { node, parentPath };
    const child = findHierarchyNode(
      (node.children || []).filter((c) => !c.isObjectGroup),
      id,
      [...parentPath, node.id],
    );
    if (child) return child;
  }
  return null;
};

/** Nested activity groups only — skips groupType / VO / BO wrappers. */
const nestedGroupChildren = (
  node: ActivitySkuHierarchyGroup,
  model: ActivityGroupsModel,
): ActivitySkuHierarchyGroup[] => (
  (node.children || []).filter((c) => !c.isObjectGroup && model.byId.has(c.id))
);

/** Unique SKUs under this scope (walks Compliance → VO/BO, etc.). */
const collectSkuChartNodes = (
  node: ActivitySkuHierarchyGroup,
  parentPath: string[],
): MetricsScopeChartNode[] => {
  const out: MetricsScopeChartNode[] = [];
  const seen = new Set<string>();
  const walk = (n: ActivitySkuHierarchyGroup, path: string[]) => {
    (n.skus || []).forEach((s) => {
      const id = String(s?.id || '').trim();
      if (!id || seen.has(id)) return;
      seen.add(id);
      out.push({
        id,
        name: String(s.name || '').trim() || id,
        parentPath: path,
        depth: path.length,
        isSku: true,
      });
    });
    (n.children || []).forEach((c) => walk(c, [...path, n.id]));
  };
  walk(node, [...parentPath, node.id]);
  return out;
};

/** Departments root — outer ring is Field operations, Workshop, HSE, … */
export const departmentRootChartNodes = (
  model: ActivityGroupsModel,
): MetricsScopeChartNode[] => {
  const named: MetricsScopeChartNode[] = [];
  const rootName = DEPARTMENTS_ROOT_SCOPE_NAME.toLowerCase();
  const walk = (
    treeNodes: ActivitySkuHierarchyGroup[],
    parentPath: string[],
  ) => {
    (treeNodes || []).forEach((node) => {
      if (named.length || node.isObjectGroup) return;
      const group = model.byId.get(node.id);
      if (
        group?.kind === 'department'
        && String(group.name || '').trim().toLowerCase() === rootName
      ) {
        named.push({
          id: node.id,
          name: node.name,
          parentPath,
          depth: parentPath.length,
        });
        return;
      }
      walk(
        (node.children || []).filter((c) => !c.isObjectGroup),
        [...parentPath, node.id],
      );
    });
  };
  walk(model.skuHierarchy, []);
  if (named.length) return named;
  return (model.skuHierarchy || [])
    .filter((root) => !root.isObjectGroup && model.byId.get(root.id)?.kind === 'department')
    .map((root) => ({ id: root.id, name: root.name, parentPath: [], depth: 0 }));
};

const skuHierarchyRoots = (model: ActivityGroupsModel): MetricsScopeChartNode[] => (
  (model.skuHierarchy || [])
    .filter((root) => !root.isObjectGroup)
    .map((root) => ({ id: root.id, name: root.name, parentPath: [], depth: 0 }))
);

/**
 * Highest fully-on scopes for the inner pie.
 * Ticking HSE stores descendant keys — lift those back to HSE, not Compliance.
 */
const highestFullySelectedScopeNodes = (
  tokens: string[],
  model: ActivityGroupsModel,
): MetricsScopeChartNode[] => {
  const expanded = new Set(expandActivityGroupsTokens(tokens, model, 'scope'));
  const roots: MetricsScopeChartNode[] = [];
  const walk = (
    nodes: ActivitySkuHierarchyGroup[],
    parentPath: string[],
  ) => {
    (nodes || []).forEach((node) => {
      if (node.isObjectGroup) return;
      const keys = collectScopeOccurrenceKeys(node, parentPath);
      const selfKey = activityOccurrenceKey(parentPath, node.id);
      const tokenHit = tokens.some((t) => (
        t === selfKey || selectionIdFromCheckedToken(t) === node.id
      ));
      const keysOn = keys.length > 0 && keys.every((k) => expanded.has(k));
      if (keysOn || tokenHit) {
        roots.push({
          id: node.id,
          name: node.name,
          parentPath,
          depth: parentPath.length,
        });
        return;
      }
      walk(
        (node.children || []).filter((c) => !c.isObjectGroup),
        [...parentPath, node.id],
      );
    });
  };
  walk(model.skuHierarchy, []);
  return roots;
};

/** Selected scope headers for inner pie — one row per top-level pick. */
export const selectedScopeChartNodes = (
  selection: MetricsScopeSelection,
  model: ActivityGroupsModel,
): MetricsScopeChartNode[] => {
  if (
    selection.scopesChecked.includes(ALL_ACTIVITY_SCOPE_ID)
    || checkedIncludesShowAll(selection.scopesChecked)
  ) {
    return skuHierarchyRoots(model);
  }
  if (selection.scopesChecked.includes(DEPARTMENTS_METRIC_SCOPE_ID)) {
    return departmentRootChartNodes(model);
  }
  const tokens = selection.scopesChecked.filter((t) => t !== ALL_ACTIVITY_SCOPE_ID);
  const lifted = highestFullySelectedScopeNodes(tokens, model);
  if (lifted.length) {
    return lifted.sort((a, b) => a.depth - b.depth || a.name.localeCompare(b.name));
  }
  const roots: MetricsScopeChartNode[] = [];
  const seen = new Set<string>();
  tokens.forEach((token) => {
    const scopeId = selectionIdFromCheckedToken(token);
    const hit = findHierarchyNode(model.skuHierarchy, scopeId);
    if (!hit || seen.has(hit.node.id)) return;
    seen.add(hit.node.id);
    roots.push({
      id: hit.node.id,
      name: hit.node.name,
      parentPath: hit.parentPath,
      depth: hit.parentPath.length,
    });
  });
  if (!roots.length && tokens.length) {
    tokens.forEach((token) => {
      const id = selectionIdFromCheckedToken(token);
      const gt = model.groupTypeById.get(id);
      const g = model.byId.get(id);
      const name = gt?.name || g?.name || id;
      if (seen.has(id)) return;
      seen.add(id);
      roots.push({ id, name, parentPath: [], depth: 0 });
    });
  }
  return roots.sort((a, b) => a.depth - b.depth || a.name.localeCompare(b.name));
};

/** Immediate child scopes, or SKUs when outerKind is 'sku' / the parent is a leaf. */
export const childScopeChartNodes = (
  parent: MetricsScopeChartNode,
  model: ActivityGroupsModel,
  selection?: MetricsScopeSelection,
): MetricsScopeChartNode[] => {
  if (parent.isSku) return [];
  const hit = findHierarchyNode(model.skuHierarchy, parent.id);
  if (!hit) return [];
  if (selection?.outerKind === 'sku') {
    return collectSkuChartNodes(hit.node, hit.parentPath);
  }
  const nested = nestedGroupChildren(hit.node, model);
  if (nested.length) {
    return nested.map((child) => ({
      id: child.id,
      name: child.name,
      parentPath: [...hit.parentPath, hit.node.id],
      depth: hit.parentPath.length + 1,
    }));
  }
  // Leaf group — outer ring is its SKUs, not groupType wrapper names.
  return collectSkuChartNodes(hit.node, hit.parentPath);
};

export const metricsChartHasChildScopes = (
  selection: MetricsScopeSelection,
  model: ActivityGroupsModel,
): boolean => {
  const parents = selectedScopeChartNodes(selection, model);
  return parents.some((p) => childScopeChartNodes(p, model, selection).length > 0);
};

const logSkuIds = (log: Log): string[] => (
  (log.skus || []).map((s) => String(s?.id || '').trim()).filter(Boolean)
);

const logMatchesScopeNode = (
  log: Log,
  node: MetricsScopeChartNode,
  model: ActivityGroupsModel,
): boolean => {
  if (node.isSku) {
    return logSkuIds(log).includes(node.id);
  }
  const hit = findHierarchyNode(model.skuHierarchy, node.id);
  if (!hit) return false;
  const scopeKeys = expandedScopeKeysForNode(model, hit.node, hit.parentPath);
  return logMatchesActivityGroupsOccurrenceKeys(log, scopeKeys, model, 'any');
};

/** Inner pie label — deepest selected scope that owns this log. */
export const metricsInnerScopeLabel = (
  log: Log,
  selection: MetricsScopeSelection,
  model: ActivityGroupsModel,
): string => {
  const parents = selectedScopeChartNodes(selection, model);
  const matches = parents.filter((p) => logMatchesScopeNode(log, p, model));
  if (!matches.length) {
    return String(log.type || 'Activity').trim() || 'Activity';
  }
  const best = matches.sort((a, b) => b.depth - a.depth || a.name.localeCompare(b.name))[0];
  return best.name;
};

/** Outer pie / non-pie series — SKU or 1st child scope under the inner pick. */
export const metricsOuterScopeLabel = (
  log: Log,
  selection: MetricsScopeSelection,
  model: ActivityGroupsModel,
): string => {
  const parents = selectedScopeChartNodes(selection, model);
  const matches = parents.filter((p) => logMatchesScopeNode(log, p, model));
  if (!matches.length) {
    return metricsInnerScopeLabel(log, selection, model);
  }
  const parent = matches.sort((a, b) => b.depth - a.depth)[0];
  const children = childScopeChartNodes(parent, model, selection);
  if (!children.length) {
    return parent.name;
  }
  const childHit = children.find((child) => logMatchesScopeNode(log, child, model));
  if (childHit) return childHit.name;
  return parent.name;
};

/** Area / line / radar series — child SKUs/scopes when present, else selected scopes. */
export const metricsSeriesScopeLabel = (
  log: Log,
  selection: MetricsScopeSelection,
  model: ActivityGroupsModel,
): string => {
  if (!metricsChartHasChildScopes(selection, model)) {
    return metricsInnerScopeLabel(log, selection, model);
  }
  return metricsOuterScopeLabel(log, selection, model);
};

/** All child names for initializing empty chart series keys. */
export const metricsChartSeriesKeys = (
  selection: MetricsScopeSelection,
  model: ActivityGroupsModel,
): string[] => {
  const parents = selectedScopeChartNodes(selection, model);
  if (!metricsChartHasChildScopes(selection, model)) {
    return parents.map((p) => p.name);
  }
  const names = new Set<string>();
  parents.forEach((p) => {
    childScopeChartNodes(p, model, selection).forEach((c) => names.add(c.name));
  });
  if (!names.size) {
    parents.forEach((p) => names.add(p.name));
  }
  return [...names].sort((a, b) => a.localeCompare(b));
};
